/**
 * Monthly Earnings Utilities
 * Helper functions for earnings calculations and data processing
 */
import { parseISO, endOfMonth, differenceInDays, min, max, format } from 'date-fns';

// Get start year from tax year format (e.g., "2024/2025" -> 2024)
export const getStartYear = (taxYear) => {
  if (!taxYear || taxYear === 'all') return new Date().getFullYear();
  return parseInt(taxYear.split('/')[0]);
};

// Calculate daily rate for a trip
export const getDailyRate = (trip) => {
  if (!trip.day_rates || trip.day_rates.length === 0) {
    return 0;
  }
  // Sum all day rates to get total daily rate
  return trip.day_rates.reduce((sum, rate) => sum + (rate.daily_rate || 0), 0);
};

// Get trip earning period — from the earliest to latest date in the composition.
// Every day in this window is an earning day.
export const getTripDates = (trip) => {
  const allDates = [];

  if (trip.date_left_uk) allDates.push(parseISO(trip.date_left_uk));
  if (trip.date_returned_uk) allDates.push(parseISO(trip.date_returned_uk));
  if (trip.date_joined_vessel) allDates.push(parseISO(trip.date_joined_vessel));
  if (trip.date_left_vessel) allDates.push(parseISO(trip.date_left_vessel));

  const stops = [
    ...(trip.pre_uk_stops || []), ...(trip.pre_abroad_stops || []),
    ...(trip.post_abroad_stops || []), ...(trip.post_uk_stops || []),
  ];
  for (const stop of stops) {
    if (stop.date) allDates.push(parseISO(stop.date));
    if (stop.date_to) allDates.push(parseISO(stop.date_to));
    if (stop.checkout_date) allDates.push(parseISO(stop.checkout_date));
  }

  // Also include day_rate boundaries
  for (const r of (trip.day_rates || [])) {
    if (r.date_from) allDates.push(parseISO(r.date_from));
    if (r.date_to) allDates.push(parseISO(r.date_to));
  }

  if (allDates.length < 2) {
    // Need at least a start and end
    const startDate = trip.date_left_uk || trip.date_joined_vessel;
    const endDate = trip.date_returned_uk || trip.date_left_vessel;
    if (!startDate || !endDate) return null;
    return { start: parseISO(startDate), end: parseISO(endDate) };
  }

  return {
    start: new Date(Math.min(...allDates.map(d => d.getTime()))),
    end: new Date(Math.max(...allDates.map(d => d.getTime())))
  };
};

// Build a set of all trip start dates for checking adjoining trips
export const getTripStartDates = (trips) => {
  const startDates = new Set();
  trips.forEach(trip => {
    const startStr = trip.date_left_uk || trip.date_joined_vessel;
    if (startStr) {
      startDates.add(startStr);
    }
  });
  return startDates;
};

// Check if a trip has an adjoining trip starting on its end date
export const hasAdjoiningTrip = (trip, tripStartDates) => {
  const endStr = trip.date_returned_uk || trip.date_left_vessel;
  const startStr = trip.date_left_uk || trip.date_joined_vessel;
  return endStr && tripStartDates.has(endStr) && endStr !== startStr;
};

// Calculate earnings for a specific day rate period within a month
export const calculateRatePeriodEarnings = (rate, monthStart, monthEnd) => {
  if (!rate.date_from || !rate.date_to || !rate.daily_rate) return 0;

  const rateStart = parseISO(rate.date_from);
  const rateEnd = parseISO(rate.date_to);
  const overlapStart = max([rateStart, monthStart]);
  const overlapEnd = min([rateEnd, monthEnd]);

  if (overlapStart > overlapEnd) return 0;
  const days = differenceInDays(overlapEnd, overlapStart) + 1;
  return days > 0 ? days * rate.daily_rate : 0;
};

// ---- Post-contract uplift logic for salaried contracts ----
// When a seafarer exceeds their contracted annual days (e.g. 120 days),
// the uplift rate switches from standard_uplift to the higher post_contract_uplift.

const calculateSalariedUpliftEarnings = (trip, monthStart, monthEnd, contracts, allTrips) => {
  const tripDates = getTripDates(trip);
  if (!tripDates) return { upliftTotal: 0, effectiveRate: 0, isPostContract: false };

  const overlapStart = max([tripDates.start, monthStart]);
  const overlapEnd = min([tripDates.end, monthEnd]);
  const daysInMonth = differenceInDays(overlapEnd, overlapStart) + 1;
  if (daysInMonth <= 0) return { upliftTotal: 0, effectiveRate: 0, isPostContract: false };

  const standardUplift = parseFloat(trip.sea_uplift_rate) || 0;

  // No contract linked → standard uplift for all days
  if (!trip.contract_id || !contracts || contracts.length === 0) {
    return { upliftTotal: daysInMonth * standardUplift, effectiveRate: standardUplift, isPostContract: false };
  }

  const contract = contracts.find(c => c.id === trip.contract_id);
  if (!contract || !contract.contract_days || contract.contract_type !== 'salaried') {
    return { upliftTotal: daysInMonth * standardUplift, effectiveRate: standardUplift, isPostContract: false };
  }

  const postUplift = contract.post_contract_uplift || standardUplift;
  const threshold = contract.contract_days;

  // Gather date ranges for all salaried trips linked to this contract
  const contractTrips = (allTrips || [])
    .filter(t => t.contract_id === trip.contract_id && t.pay_type === 'salaried')
    .map(t => { const d = getTripDates(t); return d ? { start: d.start, end: d.end } : null; })
    .filter(Boolean)
    .sort((a, b) => a.start.getTime() - b.start.getTime());

  // Count cumulative days worked before overlapStart (across all contract trips)
  let daysBefore = 0;
  for (const ct of contractTrips) {
    if (ct.end < overlapStart) {
      daysBefore += differenceInDays(ct.end, ct.start) + 1;
    } else if (ct.start < overlapStart) {
      const preEnd = new Date(overlapStart.getTime() - 86400000);
      daysBefore += differenceInDays(preEnd, ct.start) + 1;
    }
  }

  // Apply the appropriate uplift rate for each day
  let upliftTotal = 0;
  let postDays = 0;
  for (let i = 0; i < daysInMonth; i++) {
    const cumulativeDay = daysBefore + i + 1;
    if (cumulativeDay <= threshold) {
      upliftTotal += standardUplift;
    } else {
      upliftTotal += postUplift;
      postDays++;
    }
  }

  return {
    upliftTotal,
    effectiveRate: postDays > 0 ? postUplift : standardUplift,
    isPostContract: postDays > 0,
    postDays,
    standardDays: daysInMonth - postDays
  };
};

// ---- Day-by-day rate calculation ----
// For a given day, determine the highest applicable rate from all covering
// segments (day_rates, travel stops, hotel stops). Overlapping transition
// days (e.g. hotel checkout + vessel join) are counted once at the higher rate.

const getRateForDate = (trip, date, dayStr) => {
  let maxRate = 0;

  // 1. Check day_rate periods (vessel/work — typically the highest rate)
  for (const rate of (trip.day_rates || [])) {
    if (!rate.date_from || !rate.date_to) continue;
    if (date >= parseISO(rate.date_from) && date <= parseISO(rate.date_to)) {
      maxRate = Math.max(maxRate, rate.daily_rate || 0);
    }
  }

  // 2. Determine travel & hotel rates
  const baseRate = (trip.day_rates && trip.day_rates.length > 0)
    ? (trip.day_rates[0].daily_rate || 0)
    : 0;
  const travelRate = (trip.has_separate_travel_rate && trip.travel_day_rate)
    ? parseFloat(trip.travel_day_rate) || baseRate
    : baseRate;
  const hotelRate = (trip.has_separate_hotel_rate && trip.hotel_day_rate)
    ? parseFloat(trip.hotel_day_rate) || baseRate
    : baseRate;

  // 3. Check all stops (pre/post, UK/abroad)
  const allStops = [
    ...(trip.pre_uk_stops || []), ...(trip.pre_abroad_stops || []),
    ...(trip.post_abroad_stops || []), ...(trip.post_uk_stops || []),
  ];

  for (const stop of allStops) {
    if (!stop.date) continue;
    const stopDate = parseISO(stop.date);

    if ((stop.type === 'hotel' || stop.type === 'both') && (stop.date_to || stop.checkout_date)) {
      const stopEnd = parseISO(stop.date_to || stop.checkout_date);
      if (date >= stopDate && date <= stopEnd) {
        maxRate = Math.max(maxRate, hotelRate);
      }
    } else {
      // Single-day stop (travel or hotel without end date)
      if (dayStr === format(stopDate, 'yyyy-MM-dd')) {
        const stopRate = (stop.type === 'hotel' || stop.type === 'both') ? hotelRate : travelRate;
        maxRate = Math.max(maxRate, stopRate);
      }
    }
  }

  // 4. If no explicit rate but within the earning period → apply base rate
  //    (the seafarer is still engaged; the warning system flags this separately)
  if (maxRate === 0 && baseRate > 0) {
    maxRate = baseRate;
  }

  return maxRate;
};

// Calculate earnings for a specific month from a trip
// contracts and allTrips are optional — used for post-contract uplift on salaried trips
export const calculateMonthEarnings = (trip, monthStart, monthEnd, contracts = [], allTrips = []) => {
  const tripDates = getTripDates(trip);
  if (!tripDates) return 0;

  if (tripDates.end < monthStart || tripDates.start > monthEnd) return 0;

  // Day-rate trips: iterate each calendar day, apply the highest applicable rate
  if (trip.day_rates && trip.day_rates.length > 0 && trip.day_rates[0].date_from) {
    const overlapStart = max([tripDates.start, monthStart]);
    const overlapEnd = min([tripDates.end, monthEnd]);

    let totalEarnings = 0;
    let d = new Date(overlapStart);
    while (d <= overlapEnd) {
      totalEarnings += getRateForDate(trip, d, format(d, 'yyyy-MM-dd'));
      d = new Date(d.getTime() + 86400000);
    }
    return totalEarnings;
  }

  // Salaried trips: monthly base salary + uplift (with post-contract logic)
  if (trip.pay_type === 'salaried' && trip.monthly_salary) {
    const overlapStart = max([tripDates.start, monthStart]);
    const overlapEnd = min([tripDates.end, monthEnd]);
    const daysWorked = differenceInDays(overlapEnd, overlapStart) + 1;
    if (daysWorked <= 0) return 0;
    const monthlySalary = parseFloat(trip.monthly_salary) || 0;
    const { upliftTotal } = calculateSalariedUpliftEarnings(trip, monthStart, monthEnd, contracts, allTrips);
    return monthlySalary + upliftTotal;
  }

  // Fallback for trips without date-ranged day_rates
  const dailyRate = getDailyRate(trip);
  if (dailyRate === 0) return 0;

  const overlapStart = max([tripDates.start, monthStart]);
  const overlapEnd = min([tripDates.end, monthEnd]);
  const daysInMonth = differenceInDays(overlapEnd, overlapStart) + 1;
  return daysInMonth > 0 ? daysInMonth * dailyRate : 0;
};

// Get the actual contractual daily rate for a trip in a given month
// Shows the real rate the seafarer was paid, not a computed average
const getActualDailyRate = (trip, monthStart, monthEnd) => {
  if (trip.day_rates && trip.day_rates.length > 0) {
    // Find rates that overlap with this month
    const overlapping = trip.day_rates.filter(rate => {
      if (!rate.date_from || !rate.date_to) return true; // No date range = applies everywhere
      const rateStart = parseISO(rate.date_from);
      const rateEnd = parseISO(rate.date_to);
      return rateStart <= monthEnd && rateEnd >= monthStart;
    });

    if (overlapping.length > 0) {
      // Return the primary rate (first overlapping). If multiple rates exist
      // in the same month, the per-trip row shows the dominant one.
      return overlapping[0].daily_rate || 0;
    }

    // Fallback: return the first rate available
    return trip.day_rates[0].daily_rate || 0;
  }

  // For salaried trips, show the uplift rate
  if (trip.pay_type === 'salaried' && trip.sea_uplift_rate) {
    return parseFloat(trip.sea_uplift_rate) || 0;
  }

  return 0;
};

// Get trip details for a specific month
export const getTripsForMonth = (trips, monthStart, monthEnd, contracts = []) => {
  return trips.filter(trip => {
    const tripDates = getTripDates(trip);
    if (!tripDates) return false;
    
    // Check if trip overlaps with month
    return !(tripDates.end < monthStart || tripDates.start > monthEnd);
  }).map(trip => {
    const tripDates = getTripDates(trip);
    
    // Calculate calendar days in this month
    const overlapStart = max([tripDates.start, monthStart]);
    const overlapEnd = min([tripDates.end, monthEnd]);
    const daysInMonth = differenceInDays(overlapEnd, overlapStart) + 1;
    
    // Calculate earnings using the proper rate-based calculation (with contracts)
    const earnings = calculateMonthEarnings(trip, monthStart, monthEnd, contracts, trips);
    
    // Display the actual contractual rate, not a blended average
    let displayRate = getActualDailyRate(trip, monthStart, monthEnd);
    let isPostContract = false;
    let postContractRate = null;

    // For salaried trips linked to a contract, check post-contract uplift
    if (trip.pay_type === 'salaried' && trip.contract_id && contracts.length > 0) {
      const upliftInfo = calculateSalariedUpliftEarnings(trip, monthStart, monthEnd, contracts, trips);
      isPostContract = upliftInfo.isPostContract;
      if (isPostContract) {
        displayRate = upliftInfo.effectiveRate;
        postContractRate = upliftInfo.effectiveRate;
      }
    }
    
    return {
      ...trip,
      daysInMonth: daysInMonth > 0 ? daysInMonth : 0,
      dailyRate: displayRate,
      monthEarnings: earnings,
      overlapStart: format(overlapStart, 'dd MMM'),
      overlapEnd: format(overlapEnd, 'dd MMM'),
      isPostContract,
      postContractRate
    };
  }).filter(trip => trip.monthEarnings > 0 || trip.daysInMonth > 0);
};

// Calculate monthly data for the selected tax year (April to April)
export const calculateMonthlyData = (trips, selectedYear, contracts = []) => {
  const startYear = getStartYear(selectedYear);
  const months = [];
  
  // Tax year runs April (startYear) to March (startYear + 1)
  // Month 0 = April, Month 11 = March
  for (let i = 0; i < 12; i++) {
    const monthIndex = (i + 3) % 12; // Start from April (month 3)
    const year = i < 9 ? startYear : startYear + 1; // Apr-Dec is startYear, Jan-Mar is startYear+1
    
    const monthStart = new Date(year, monthIndex, 1);
    const monthEnd = endOfMonth(monthStart);
    
    let totalEarnings = 0;
    trips.forEach(trip => {
      totalEarnings += calculateMonthEarnings(trip, monthStart, monthEnd, contracts, trips);
    });
    
    months.push({
      month: format(monthStart, 'MMM'),
      fullMonth: format(monthStart, 'MMMM yyyy'),
      monthKey: format(monthStart, 'yyyy-MM'),
      earnings: totalEarnings,
      monthStart,
      monthEnd
    });
  }
  
  return months;
};

// Calculate annual data across all tax years
export const calculateAnnualData = (trips, taxYears, contracts = []) => {
  if (!taxYears || taxYears.length === 0) return [];
  
  return taxYears.map(taxYear => {
    const monthlyData = calculateMonthlyData(trips, taxYear, contracts);
    const totalEarnings = monthlyData.reduce((sum, m) => sum + m.earnings, 0);
    const monthsWithEarnings = monthlyData.filter(m => m.earnings > 0).length;
    
    // Count trips in this tax year
    const startYear = parseInt(taxYear.split('/')[0]);
    const yearStart = new Date(startYear, 3, 1); // April 1
    const yearEnd = new Date(startYear + 1, 2, 31, 23, 59, 59); // March 31
    
    const tripsInYear = trips.filter(trip => {
      const tripDates = getTripDates(trip);
      if (!tripDates) return false;
      return !(tripDates.end < yearStart || tripDates.start > yearEnd);
    });
    
    return {
      taxYear,
      totalEarnings,
      monthsWithEarnings,
      tripCount: tripsInYear.length,
      monthlyData
    };
  });
};
